import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DT } from '../../theme/designTokens';
import { PrimaryButton } from '../PrimaryButton';
import type { RoundEndPayload } from '../../types/roundEnd';
import type { RevealedHandTile, Player, RevealedPlayer } from '../../types/game';
import { PlayerAvatar } from '../ui/PlayerAvatar';
import { CountryFlag } from '../ui/CountryFlag';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import { getViewerHandAssetPath } from '../../config/viewerTileSkinAssets';
import type { ViewerTileSkin } from '../../config/viewerCosmetics';
import { useTranslation } from '../../i18n/react';
import { t as translate } from '../../i18n';
import { getPlayerName } from '../../utils/playerName';

const COUNTDOWN_SECONDS = 15;
/** Localized team labels — this legacy overlay's historical treatment is
 *  UPPERCASE, applied here so English output stays character-identical
 *  ('TEAM A') while the shared base label localizes ('EQUIPO A'). */
const teamLabel = (team: number): string =>
  String(translate(team === 0 ? 'gameplay:team.a' : 'gameplay:team.b')).toUpperCase();
const TEAM_COLOR  = [DT.color.teamRed, DT.color.teamBlue] as const;

interface Props {
  payload:         RoundEndPayload;
  /** Mark THIS viewer ready for the next round. Does NOT create or navigate to
   *  the next game — the server gates the transition (all humans ready, or the
   *  server deadline), and every client advances together on next_game_created. */
  onReady:         (sessionId: number) => Promise<void>;
  onBackHome:      () => void;
  /** Final-match rematch vote. Submits once; navigation follows ONLY the
   *  authoritative backend result (response/broadcast), never local state.
   *  Absent/undefined → the legacy Back Home button renders instead. */
  onRematchVote?:  (sessionId: number, vote: 'yes' | 'no') => Promise<void>;
  /** Cosmetics: viewer-selected tile skin (durable prop). Defaults to
   *  'classic'. Current selection source is temporary — see
   *  viewerCosmetics.ts. */
  viewerTileSkin?: ViewerTileSkin;
}

// ── Tile asset helpers ────────────────────────────────────────────────────────

function tileUrl(a: number, b: number, skin: ViewerTileSkin = 'classic'): string {
  // Resolver handles classic + every non-classic skin (red/black/rainbow).
  return getViewerHandAssetPath(skin, a, b);
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Seat avatar — the shared PlayerAvatar (authoritative avatar_key or the
 *  canonical display-name initial) inside the overlay's team-colored circle. */
function SeatAvatar({ name, team, avatarKey }: { name: string; team: number | null; avatarKey?: string | null }) {
  const bg = team !== null ? TEAM_COLOR[team as 0 | 1] : DT.color.textMuted;
  return (
    <PlayerAvatar
      avatarKey={avatarKey}
      name={name}
      size={36}
      loading="eager"
      initialStyle={{
        background: bg, border: 'none',
        fontSize: 14, fontWeight: 700, fontFamily: DT.font.sans,
        color: '#fff', opacity: 0.92,
      }}
    />
  );
}

function MiniTile({ tile, delay, viewerTileSkin }: { tile: RevealedHandTile; delay: number; viewerTileSkin?: ViewerTileSkin }) {
  return (
    <img
      src={tileUrl(tile.value_a, tile.value_b, viewerTileSkin)}
      alt={`${tile.value_a}-${tile.value_b}`}
      // Cosmetics (durable): non-classic reveal art falls back to classic on
      // load failure so a missing/broken asset never blocks the round-end
      // overlay from rendering. Only attached for a non-classic viewer skin —
      // a viewer resolved to 'classic' renders with no onError handler at
      // all, byte-for-byte as before this skin plumbing existed. onerror is
      // nulled out FIRST so the fallback fires at most once per tile —
      // comparing img.src (always browser-resolved to an absolute URL)
      // against the relative classic path would never match, making a
      // same-reference guard unreliable here.
      onError={viewerTileSkin !== 'classic' ? (e) => {
        const img = e.currentTarget;
        img.onerror = null;
        img.src = tileUrl(tile.value_a, tile.value_b, 'classic');
      } : undefined}
      style={{
        height:          28,
        width:           'auto',
        borderRadius:    3,
        imageRendering:  'crisp-edges' as never,
        flexShrink:      0,
        animation:       `reo-tile-in 200ms ${DT.motion.easing} both`,
        animationDelay:  `${delay}ms`,
      }}
    />
  );
}

function DominoBadge() {
  return (
    <span style={{
      display:       'inline-flex',
      alignItems:    'center',
      padding:       '2px 9px',
      borderRadius:  DT.radius.pill,
      background:    'rgba(201,168,76,0.12)',
      border:        `1px solid ${DT.color.borderGold}`,
      fontSize:      10,
      fontWeight:    700,
      fontFamily:    DT.font.sans,
      color:         DT.color.gold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as never,
      whiteSpace:    'nowrap' as never,
    }}>
      {String(translate('gameplay:roundEnd.dominoedBadge')).toLowerCase()}
    </span>
  );
}

// Revealed row — shows actual domino images.
function RevealedRow({
  name, team, tiles, isDomino, isWinner, rowDelay, viewerTileSkin, avatarKey, countryCode, isBot,
}: {
  name: string; team: number | null; tiles: RevealedHandTile[];
  isDomino: boolean; isWinner: boolean; rowDelay: number; viewerTileSkin?: ViewerTileSkin;
  avatarKey?: string | null; countryCode?: string | null; isBot?: boolean;
}) {
  const borderColor = isWinner ? DT.color.borderGoldStrong : DT.color.border;
  const nameColor   = isWinner ? DT.color.gold : DT.color.textPrimary;

  return (
    <div style={{
      background:   DT.color.cardBgElevated,
      border:       `1px solid ${borderColor}`,
      borderLeft:   team !== null ? `3px solid ${TEAM_COLOR[team as 0 | 1]}` : `1px solid ${borderColor}`,
      borderRadius: DT.radius.md,
      padding:      '12px 14px',
      display:      'flex',
      flexDirection:'column',
      gap:          8,
      animation:    `reo-row-in 280ms ${DT.motion.easing} both`,
      animationDelay: `${rowDelay}ms`,
    }}>
      {/* Top row: flag + avatar + name + badge. Bots keep the flag slot's
          width reserved (empty) so the tile strip below stays aligned
          whether or not a flag renders. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {isBot
          ? <span aria-hidden="true" style={{ width: 18, flexShrink: 0 }} />
          : <CountryFlag countryCode={countryCode} width={18} />}
        <SeatAvatar name={name} team={team} avatarKey={avatarKey} />
        <div style={{
          flex:         1,
          minWidth:     0,
          fontSize:     13,
          fontWeight:   600,
          fontFamily:   DT.font.sans,
          color:        nameColor,
          overflow:     'hidden',
          textOverflow: 'ellipsis',
          whiteSpace:   'nowrap',
        }}>
          {name}
        </div>
        {isDomino && <DominoBadge />}
        {!isDomino && tiles.length > 0 && (
          <span style={{
            fontSize:   11,
            fontFamily: DT.font.sans,
            color:      DT.color.textMuted,
            flexShrink: 0,
          }}>
            {translate('gameplay:roundEnd.tiles', { count: tiles.length })}
          </span>
        )}
      </div>

      {/* Tile strip */}
      {tiles.length > 0 && (
        <div style={{
          display:    'flex',
          flexWrap:   'wrap',
          gap:        5,
          paddingLeft: 74, // align under the name (flag 18 + gap 10 + avatar 36 + gap 10)
        }}>
          {tiles.map((t, ti) => (
            <MiniTile key={t.id} tile={t} delay={rowDelay + 60 + ti * 35} viewerTileSkin={viewerTileSkin} />
          ))}
        </div>
      )}
    </div>
  );
}

// Count-only row — fallback when revealed_players not present.
function CountRow({
  name, team, count, isDomino, isWinner, rowDelay, avatarKey, countryCode, isBot,
}: {
  name: string; team: number | null; count: number | null;
  isDomino: boolean; isWinner: boolean; rowDelay: number;
  avatarKey?: string | null; countryCode?: string | null; isBot?: boolean;
}) {
  const borderColor = isWinner ? DT.color.borderGoldStrong : DT.color.border;
  const nameColor   = isWinner ? DT.color.gold : DT.color.textPrimary;

  return (
    <div style={{
      background:   DT.color.cardBgElevated,
      border:       `1px solid ${borderColor}`,
      borderLeft:   team !== null ? `3px solid ${TEAM_COLOR[team as 0 | 1]}` : `1px solid ${borderColor}`,
      borderRadius: DT.radius.md,
      padding:      '12px 14px',
      display:      'flex',
      alignItems:   'center',
      gap:          10,
      animation:    `reo-row-in 280ms ${DT.motion.easing} both`,
      animationDelay: `${rowDelay}ms`,
    }}>
      {!isBot && <CountryFlag countryCode={countryCode} width={18} />}
      <SeatAvatar name={name} team={team} avatarKey={avatarKey} />
      <div style={{
        flex:         1,
        minWidth:     0,
        fontSize:     13,
        fontWeight:   600,
        fontFamily:   DT.font.sans,
        color:        nameColor,
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
      }}>
        {name}
      </div>
      {isDomino ? (
        <DominoBadge />
      ) : count !== null ? (
        <span style={{
          fontSize:   12,
          fontFamily: DT.font.sans,
          fontWeight: 600,
          color:      DT.color.textMuted,
          flexShrink: 0,
        }}>
          {translate('gameplay:roundEnd.tiles', { count })}
        </span>
      ) : null}
    </div>
  );
}

// ── Remaining section ─────────────────────────────────────────────────────────

function RemainingSection({
  players, revealedPlayers, handCounts, winnerTeam, winType, viewerTileSkin,
}: Pick<RoundEndPayload, 'players' | 'revealedPlayers' | 'handCounts' | 'winnerTeam' | 'winType'> & { viewerTileSkin?: ViewerTileSkin }) {
  const hasRevealed = revealedPlayers.length > 0;
  const hasPlayers  = players.length > 0;
  const hasCounts   = Object.keys(handCounts).length > 0;

  if (!hasPlayers && !hasCounts) return null;

  // Build sorted render list from players (or synthesize from handCounts).
  const rowsFromPlayers = hasPlayers
    ? [...players].sort((a, b) => a.position - b.position)
    : [];

  const countOnlyPositions = !hasPlayers
    ? Object.keys(handCounts).sort((a, b) => Number(a) - Number(b))
    : [];

  const sectionLabel = translate(hasRevealed ? 'gameplay:roundEnd.handsRevealed' : 'gameplay:roundEnd.remainingTiles');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          fontSize:      11,
          fontWeight:    700,
          fontFamily:    DT.font.sans,
          letterSpacing: '0.3em',
          textTransform: 'uppercase' as never,
          color:         DT.color.textMuted,
        }}>
          {sectionLabel}
        </div>
        <div style={{
          flex:       1,
          height:     1,
          background: DT.color.border,
        }} />
      </div>

      {/* Count-only path (no players list) */}
      {countOnlyPositions.map((posStr, i) => {
        const pos      = Number(posStr);
        const count    = handCounts[posStr];
        const rp       = revealedPlayers.find(r => r.position === pos);
        const isDomino = winType === 'win' && count === 0;
        const name     = rp ? getPlayerName(rp, `Seat ${pos + 1}`) : `Seat ${pos + 1}`;
        return (
          <CountRow
            key={posStr}
            name={name}
            team={null}
            count={count}
            isDomino={isDomino}
            isWinner={false}
            rowDelay={i * 70}
            avatarKey={rp?.avatar_key ?? null}
            countryCode={rp?.country_code ?? null}
            isBot={rp?.is_bot ?? false}
          />
        );
      })}

      {/* Players path — prefer revealed tiles, fall back to count */}
      {rowsFromPlayers.map((player: Player, i: number) => {
        const rp        = revealedPlayers.find((r: RevealedPlayer) => r.position === player.position);
        const count     = handCounts[String(player.position)] ?? null;
        const isWinner  = winnerTeam !== null && player.team === winnerTeam;
        const isDomino  = winType === 'win'
          && player.team === winnerTeam
          && (rp !== undefined ? rp.tiles.length === 0 : count === 0);
        // Canonical name from the payload — never re-derived from email.
        const name = getPlayerName(rp ?? player, `P${player.position}`);
        const avatarKey   = rp?.avatar_key ?? player.avatar_key ?? null;
        const countryCode = rp?.country_code ?? player.country_code ?? null;
        const isBot       = rp?.is_bot ?? player.is_bot ?? false;

        if (rp !== undefined && !isDomino) {
          // PRIMARY: show actual tile images
          return (
            <RevealedRow
              key={player.position}
              name={name}
              team={player.team}
              tiles={rp.tiles}
              isDomino={isDomino}
              isWinner={isWinner}
              rowDelay={i * 70}
              viewerTileSkin={viewerTileSkin}
              avatarKey={avatarKey}
              countryCode={countryCode}
              isBot={isBot}
            />
          );
        }

        // FALLBACK or DOMINO (empty hand): count row
        return (
          <CountRow
            key={player.position}
            name={name}
            team={player.team}
            count={isDomino ? 0 : count}
            isDomino={isDomino}
            isWinner={isWinner}
            rowDelay={i * 70}
            avatarKey={avatarKey}
            countryCode={countryCode}
            isBot={isBot}
          />
        );
      })}
    </div>
  );
}

// ── Main overlay ──────────────────────────────────────────────────────────────

export function RoundEndOverlay({ payload, onReady, onBackHome, onRematchVote, viewerTileSkin }: Props) {
  const { t } = useTranslation('gameplay');
  const {
    roundNumber, winnerTeam, winType, myTeam, myPosition,
    teamScores, previousTeamScores,
    players, revealedPlayers, handCounts,
    sessionOver, sessionWinnerTeam, targetScore, sessionId,
    nextRoundDeadlineAt, readyPositions,
    rematch, myUserId,
  } = payload;

  const didWin     = winnerTeam === myTeam;
  const sessionWon = sessionOver && sessionWinnerTeam === myTeam;

  // ── Final-match rematch vote state ──────────────────────────────────────
  // The authoritative snapshot lives in payload.rematch (merged live by
  // useRoundEndOverlay from 'phaser:rematch-state'). The viewer's recorded
  // vote is restored from the user-id-keyed votes map on reconnect.
  const hasRematchFlow = sessionOver && !!rematch && !!onRematchVote;
  const myRecordedVote: 'yes' | 'no' | null =
    (rematch && myUserId ? rematch.votes[String(myUserId)] : null) ?? null;
  // Local pending marker between button press and the authoritative echo.
  const [pendingVote, setPendingVote] = useState<'yes' | 'no' | null>(null);
  const myVote = myRecordedVote ?? pendingVote;

  const castVote = useCallback((vote: 'yes' | 'no') => {
    if (!onRematchVote || myRecordedVote || pendingVote) return;
    setPendingVote(vote);
    void onRematchVote(sessionId, vote);
  }, [onRematchVote, myRecordedVote, pendingVote, sessionId]);

  // Rematch decision countdown — derived from the PERSISTED server deadline
  // (rematch.deadline_at), never a fresh 20s local timer: a refresh restores
  // the true remaining time, and background-tab throttling or clock drift
  // cannot change the outcome because this is DISPLAY-ONLY — resolution
  // always arrives as the authoritative backend broadcast/state.
  const rematchDeadlineMs = useMemo(
    () => (rematch?.deadline_at ? Date.parse(rematch.deadline_at) : null),
    [rematch?.deadline_at],
  );
  const rematchRemaining = useCallback(
    () => (rematchDeadlineMs == null ? null : Math.max(0, Math.ceil((rematchDeadlineMs - Date.now()) / 1000))),
    [rematchDeadlineMs],
  );
  const [rematchCountdown, setRematchCountdown] = useState<number | null>(rematchRemaining);
  useEffect(() => {
    if (!sessionOver || rematch?.state !== 'pending' || rematchDeadlineMs == null) return;
    setRematchCountdown(rematchRemaining());
    const t = setInterval(() => setRematchCountdown(rematchRemaining()), 250);
    return () => clearInterval(t);
  }, [sessionOver, rematch?.state, rematchDeadlineMs, rematchRemaining]);

  const rejectionDestination = String(translate(
    payload.gameMode === 'bots' ? 'navigation:items.playBots' : 'navigation:items.playOnline'));

  // Countdown is DISPLAY-ONLY and derived from the SERVER deadline — the client
  // timer is never authoritative and never creates the next round. When the
  // deadline is absent (older backend) we fall back to a local 15 s target
  // purely for display; the round still only advances via next_game_created.
  const deadlineMs = useMemo(() => (
    nextRoundDeadlineAt ? Date.parse(nextRoundDeadlineAt) : Date.now() + COUNTDOWN_SECONDS * 1000
  ), [nextRoundDeadlineAt]);
  const remaining = useCallback(
    () => Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000)),
    [deadlineMs],
  );
  const [countdown, setCountdown] = useState(remaining);

  useEffect(() => {
    if (sessionOver) return;
    setCountdown(remaining());
    const t = setInterval(() => setCountdown(remaining()), 250);
    return () => clearInterval(t);
  }, [sessionOver, remaining]);

  // Round-transition readiness for THIS viewer. Restored from the authoritative
  // readyPositions so a reconnect/refresh keeps the "Ready ✓" state.
  const initialReady = (readyPositions ?? []).includes(myPosition);
  const readyRef = useRef(initialReady);
  const [ready, setReady] = useState(initialReady);

  const markReady = useCallback(() => {
    if (readyRef.current) return;      // prevent duplicate readiness requests
    readyRef.current = true;
    setReady(true);
    void onReady(sessionId);
  }, [onReady, sessionId]);

  // Session-over path: the button leaves to the menu (unchanged behavior).
  const leavingRef = useRef(false);
  const [leaving, setLeaving] = useState(false);
  const backHome = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    onBackHome();
  }, [onBackHome]);

  const dominoPlayer = (() => {
    if (winType !== 'win') return null;
    const rp = revealedPlayers.find(r => r.tiles.length === 0);
    if (rp) return getPlayerName(rp);
    const zeroPos = Object.entries(handCounts).find(([, v]) => v === 0)?.[0];
    if (zeroPos == null) return null;
    const p = players.find(pl => String(pl.position) === zeroPos);
    // canonical name only — no email re-derivation
    return p ? getPlayerName(p, String(translate('gameplay:fallback.player'))) : null;
  })();

  const winnerColor = winnerTeam !== null && didWin ? DT.color.gold : DT.color.textSecondary;
  const delta = (team: number) =>
    (teamScores[String(team)] ?? 0) - (previousTeamScores[String(team)] ?? 0);

  return (
    <>
      {/* Keyframes — injected once alongside the overlay */}
      <style>{`
        @keyframes reo-row-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes reo-tile-in {
          from { opacity: 0; transform: scale(0.80); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div
        style={{
          position:                'fixed',
          inset:                   0,
          zIndex:                  DT.zIndex.overlay,
          background:              'rgba(0,0,0,0.93)',
          overflowY:               'auto',
          WebkitOverflowScrolling: 'touch' as never,
        }}
      >
        <div
          style={{
            maxWidth:      480,
            width:         '100%',
            margin:        '0 auto',
            padding:       '36px 20px',
            paddingBottom: 'max(env(safe-area-inset-bottom), 36px)',
            display:       'flex',
            flexDirection: 'column',
            gap:           20,
          }}
        >

          {/* ── Round / session label ──────────────────────────────── */}
          <div style={{
            textAlign:     'center',
            fontSize:      11,
            fontWeight:    700,
            fontFamily:    DT.font.sans,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color:         DT.color.textMuted,
          }}>
            {sessionOver ? t('legacyEnd.sessionOver') : t('legacyEnd.round', { n: roundNumber })}
          </div>

          {/* ── Winner headline ───────────────────────────────────── */}
          {sessionOver && rematch ? (
            // Final-match presentation (approved wording): winners get a
            // congratulations state, losers a respectful final result, both
            // with the final team scores.
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize:   44,
                fontWeight: 400,
                fontFamily: DT.font.heading,
                lineHeight: 1.05,
                color:      sessionWon ? DT.color.gold : DT.color.textPrimary,
              }}>
                {sessionWon ? t('legacyEnd.congrats') : t('legacyEnd.matchComplete')}
              </div>
              <div style={{
                fontSize:   15,
                fontFamily: DT.font.sans,
                color:      DT.color.textSecondary,
                marginTop:  10,
              }}>
                {(() => {
                  const winScore  = teamScores[String(sessionWinnerTeam ?? 0)] ?? 0;
                  const loseScore = teamScores[String(sessionWinnerTeam === 0 ? 1 : 0)] ?? 0;
                  return sessionWon
                    ? t('legacyEnd.yourTeamWon', { w: winScore, l: loseScore })
                    : t('legacyEnd.teamWon', {
                        team: sessionWinnerTeam !== null ? teamLabel(sessionWinnerTeam) : t('legacyEnd.otherTeam'),
                        w: winScore, l: loseScore,
                      });
                })()}
              </div>
            </div>
          ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize:   52,
              fontWeight: 400,
              fontFamily: DT.font.heading,
              lineHeight: 1,
              color:      winnerColor,
            }}>
              {winnerTeam !== null ? teamLabel(winnerTeam) : t('legacyEnd.blockedCaps')}
            </div>
            {winnerTeam !== null && (
              <div style={{
                fontSize:      13,
                fontWeight:    700,
                fontFamily:    DT.font.sans,
                color:         winnerColor,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                marginTop:     4,
              }}>
                {t('legacyEnd.wins')}
              </div>
            )}
            <div style={{
              fontSize:   13,
              fontFamily: DT.font.sans,
              color:      DT.color.textMuted,
              marginTop:  10,
            }}>
              {winType === 'win'     && dominoPlayer && t('legacyEnd.dominoed', { name: dominoPlayer })}
              {winType === 'blocked' && t('roundEnd.blockedCounted')}
              {winType === null      && ' '}
            </div>
          </div>
          )}

          {/* ── Score progression ──────────────────────────────────── */}
          <div style={{
            background:    DT.color.cardBg,
            border:        `1px solid ${DT.color.border}`,
            borderRadius:  DT.radius.lg,
            padding:       '16px 20px',
            display:       'flex',
            flexDirection: 'column',
            gap:           14,
          }}>
            {([0, 1] as const).map(team => {
              const before   = previousTeamScores[String(team)] ?? 0;
              const d        = delta(team);
              const after    = teamScores[String(team)] ?? 0;
              const isWinner = winnerTeam === team;
              const numColor = isWinner ? DT.color.gold : DT.color.textPrimary;
              return (
                <div key={team} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width:        6,
                    height:       6,
                    borderRadius: '50%',
                    background:   TEAM_COLOR[team],
                    flexShrink:   0,
                  }} />
                  <div style={{
                    width:         56,
                    flexShrink:    0,
                    fontSize:      11,
                    fontWeight:    700,
                    fontFamily:    DT.font.sans,
                    color:         TEAM_COLOR[team],
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}>
                    {teamLabel(team)}
                  </div>
                  <div style={{
                    flex:       1,
                    display:    'flex',
                    alignItems: 'center',
                    gap:        6,
                    fontFamily: DT.font.heading,
                    fontSize:   20,
                  }}>
                    <span style={{ color: DT.color.textSecondary }}>{before}</span>
                    <span style={{ color: DT.color.textFaint, fontSize: 12 }}>+</span>
                    <span style={{ color: d > 0 ? DT.color.gold : DT.color.textMuted }}>{d}</span>
                    <span style={{ color: DT.color.textFaint, fontSize: 12 }}>=</span>
                    <span style={{ color: numColor, fontWeight: 400 }}>{after}</span>
                  </div>
                </div>
              );
            })}

            {targetScore > 0 && (
              <div style={{
                fontSize:   11,
                fontFamily: DT.font.sans,
                color:      DT.color.textFaint,
                textAlign:  'right',
              }}>
                {t('legacyEnd.firstTo', { target: targetScore })}
              </div>
            )}
          </div>

          {/* ── Remaining hands / tiles ────────────────────────────── */}
          <RemainingSection
            players={players}
            revealedPlayers={revealedPlayers}
            handCounts={handCounts}
            winnerTeam={winnerTeam}
            winType={winType}
            viewerTileSkin={viewerTileSkin}
          />

          {/* ── CTA ───────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            {sessionOver && hasRematchFlow && rematch ? (
              rematch.state === 'rejected' ? (
                <div role="status" style={{
                  textAlign:  'center',
                  fontSize:   13,
                  fontFamily: DT.font.sans,
                  color:      DT.color.textSecondary,
                }}>
                  {t('matchEnd.declined', { dest: rejectionDestination })}
                </div>
              ) : rematch.state === 'accepted' ? (
                <div role="status" style={{
                  textAlign:  'center',
                  fontSize:   13,
                  fontFamily: DT.font.sans,
                  color:      DT.color.gold,
                }}>
                  {t('matchEnd.accepted')}
                </div>
              ) : (
                <>
                  <div style={{
                    textAlign:  'center',
                    fontSize:   16,
                    fontWeight: 700,
                    fontFamily: DT.font.sans,
                    color:      DT.color.textPrimary,
                  }}>
                    {t('legacyEnd.rematchQ')}
                  </div>
                  <div style={{
                    textAlign:  'center',
                    fontSize:   12,
                    fontFamily: DT.font.sans,
                    color:      DT.color.textMuted,
                    marginTop:  -4,
                  }}>
                    {t('legacyEnd.allMustAccept')}
                  </div>
                  <PrimaryButton onClick={() => castVote('yes')} disabled={myVote !== null}>
                    {myVote === 'yes' ? t('legacyEnd.rematchAccepted') : t('matchEnd.rematch')}
                  </PrimaryButton>
                  <button
                    type="button"
                    onClick={() => castVote('no')}
                    disabled={myVote !== null}
                    style={{
                      padding:      '12px 16px',
                      borderRadius: DT.radius.md,
                      background:   'transparent',
                      border:       `1px solid ${DT.color.border}`,
                      color:        myVote === 'no' ? DT.color.error : DT.color.textSecondary,
                      fontSize:     14,
                      fontWeight:   700,
                      fontFamily:   DT.font.sans,
                      cursor:       myVote !== null ? 'default' : 'pointer',
                      opacity:      myVote !== null && myVote !== 'no' ? 0.5 : 1,
                    }}
                  >
                    {myVote === 'no' ? t('matchEnd.leaving') : t('matchEnd.leave')}
                  </button>
                  <div role="status" style={{
                    textAlign:  'center',
                    fontSize:   12,
                    fontFamily: DT.font.sans,
                    color:      DT.color.textMuted,
                  }}>
                    {t('matchEnd.voteCount', { yes: rematch.yes_count, req: rematch.required_human_count })}
                    {rematchCountdown != null && (
                      // Display-only countdown from the PERSISTED server
                      // deadline. It never resolves or navigates by itself —
                      // the backend broadcast does.
                      <> · {t('legacyEnd.returningIn', { dest: rejectionDestination, n: rematchCountdown })}</>
                    )}
                  </div>
                </>
              )
            ) : sessionOver ? (
              <PrimaryButton onClick={backHome} disabled={leaving} loading={leaving}>
                {sessionWon ? t('legacyEnd.wonBackHome') : t('legacyEnd.overBackHome')}
              </PrimaryButton>
            ) : (
              <>
                <PrimaryButton onClick={markReady} disabled={ready}>
                  {ready ? t('roundEnd.readyDone') : t('legacyEnd.nextRound')}
                </PrimaryButton>
                <div style={{
                  textAlign:  'center',
                  fontSize:   12,
                  fontFamily: DT.font.sans,
                  color:      DT.color.textMuted,
                }}>
                  {countdown > 0
                    ? (ready
                        ? t('legacyEnd.waitingOthers', { n: countdown })
                        : t('legacyEnd.nextIn', { n: countdown }))
                    : t('roundEnd.startingNext')}
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
